import { IImage } from "@faire/web-api/indigofair/data/IImage";
import { MIN_WIDTH_BREAKPOINTS } from "@faire/web/common/media";
import { SafelySetInnerHTML } from "@faire/web/common/SafelySetInnerHTML";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { optimizeImageUrl } from "@faire/web/common/transform-images";
import { Weight } from "@faire/web/slate/Font";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { TypographyStyles } from "@faire/web/ui/Typography";
import { observer } from "mobx-react";
import React, { useMemo } from "react";
import snarkdown from "snarkdown";
import styled from "styled-components";

const BannerBase = (props: IProps) => {
  const { collection, dataTestId } = props;

  const dangerouslySetInnerHTML = useMemo((): string | undefined => {
    if (!collection?.header_text) {
      return undefined;
    }

    const processedMarkdown = snarkdown(collection.header_text);
    return processedMarkdown;
  }, [collection?.header_text]);

  const isSSR = useIsSSR();

  return (
    <Wrapper
      $bgColor={collection?.header_background_color}
      data-test-id={dataTestId}
    >
      <Content>
        <Left>
          <TextContent>
            <Title>{collection?.name}</Title>
            <Description>
              {!isSSR ? (
                <SafelySetInnerHTML html={dangerouslySetInnerHTML} tag="span" />
              ) : null}
            </Description>
          </TextContent>
        </Left>
        {collection?.image ? (
          <Picture>
            <source
              srcSet={optimizeImageUrl(collection?.image, isSSR, {
                width: 720,
                height: 280,
                resizeMethod: "fill",
              })}
              media="(min-width: 1280px)"
            />
            <source
              srcSet={optimizeImageUrl(collection?.image, isSSR, {
                width: 640,
                height: 280,
                resizeMethod: "fill",
              })}
              media={`(min-width: ${MIN_WIDTH_BREAKPOINTS.TABLET}px)`}
            />
            <source
              srcSet={optimizeImageUrl(collection?.image, isSSR, {
                width: 425,
                height: 182,
                resizeMethod: "fill",
              })}
              media={`(max-width: ${MIN_WIDTH_BREAKPOINTS.TABLET - 1}px)`}
            />
            <img
              src={optimizeImageUrl(collection?.image, isSSR, {
                width: 720,
                height: 280,
                resizeMethod: "fill",
              })}
              alt={collection?.name}
            />
          </Picture>
        ) : null}
      </Content>
    </Wrapper>
  );
};

export const Banner = observer(BannerBase);

interface IProps {
  collection: ICollection | undefined;
  dataTestId?: string;
}

interface ICollection {
  name: string | undefined;
  header_text: string | undefined;
  header_background_color: string | undefined;
  image: IImage | undefined;
}

const Wrapper = styled.section<{ $bgColor: string | undefined }>`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  align-items: center;
  background: ${(props) => props.$bgColor ?? "white"};
`;

const Content = styled.div`
  display: flex;
  flex-direction: column-reverse;
  align-items: stretch;
  align-self: stretch;

  ${responsiveMediaQueries.tabletAndAbove} {
    flex-direction: row;
    max-width: 1440px;
    height: 280px;
  }

  ${responsiveMediaQueries.xLargeAndAbove} {
    width: 100%;
    align-self: center;
  }
`;

const Left = styled.div`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  flex: 1;

  ${responsiveMediaQueries.tabletAndAbove} {
    align-self: center;
  }
`;

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px 16px;
  margin: -8px 0;
  width: 100%;

  ${responsiveMediaQueries.tabletAndAbove} {
    flex: 1;
    max-width: 640px;
    padding: 0 40px;
  }

  @media (min-width: 1280px) {
    padding: 0 48px;
    margin: -12px 0;
  }
`;

const Title = styled.h1`
  ${TypographyStyles({
    h2: true,
    serif: true,
    weight: "book",
    color: "white",
  })};
  line-height: 1;
  letter-spacing: 1px;
  margin: 8px 0;

  @media (min-width: 1280px) {
    margin: 12px 0;
  }
`;

const Description = styled.p`
  ${TypographyStyles({
    h4: true,
    weight: "book",
    color: "white",
  })};
  line-height: 24px;
  margin: 8px 0;

  @media (min-width: 1280px) {
    margin: 12px 0;
  }

  em,
  i {
    font-style: italic;
  }

  strong,
  b {
    font-weight: ${Weight.medium};
  }

  a {
    text-decoration: underline;
  }

  ol {
    list-style: decimal;
  }

  ul {
    list-style: disc;
  }
`;

const Picture = styled.picture`
  flex: 1;
  width: 100%;
  padding-top: 42.67%;
  position: relative;

  img {
    object-fit: cover;
    object-position: center;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
  }

  ${responsiveMediaQueries.tabletAndAbove} {
    width: unset;
    padding-top: unset;
  }
`;
